import React, { useState, useEffect, useRef } from 'react'
import ContainerDashboard from '../../components/layouts/ContainerDashboard'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { axiosInstance } from '../Config'

const avatarColors = ['#A44CD7', '#0CACC2', '#F55145', '#F9BE22', '#8A3BBD', '#19C37D', '#5A79DF', '#EC2434']
const getInitials = (name = '') => name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
const ALL_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
const DURATIONS = ['30 min', '1 hour', '1.5 hours', '2 hours', '2.5 hours', '3 hours']

const normalizeDays = (d) => {
    if (Array.isArray(d)) return d
    if (!d) return []
    try { const p = JSON.parse(d); if (Array.isArray(p)) return p } catch {}
    return String(d).split(',').map(s => s.trim()).filter(Boolean)
}

const formatTime = (value) => {
    if (!value) return ''
    const [h, m] = value.split(':').map(Number)
    const ampm = h >= 12 ? 'PM' : 'AM'
    const hour = h % 12 || 12
    return `${hour}:${String(m).padStart(2, '0')} ${ampm}`
}

const TutorAvailability = () => {
    const { tutorId } = useParams()
    const navigate = useNavigate()

    const [tutor, setTutor] = useState(null)
    const [availability, setAvailability] = useState([])
    const [showModal, setShowModal] = useState(false)
    const [editingId, setEditingId] = useState(null)

    const [days, setDays] = useState([])
    const [daysOpen, setDaysOpen] = useState(false)
    const [time, setTime] = useState('')
    const [duration, setDuration] = useState('')
    const [errors, setErrors] = useState({})
    const [saving, setSaving] = useState(false)

    const daysRef = useRef(null)

    useEffect(() => {
        document.title = 'Tutor Availability | Mindora'
        window.scrollTo(0, 0)
        Promise.all([
            axiosInstance.get(`/tutors/${tutorId}`),
            axiosInstance.get(`/tutors/${tutorId}/availability`)
        ]).then(([tutorRes, availRes]) => {
            setTutor(tutorRes.data?.data ?? tutorRes.data)
            const data = availRes.data?.data ?? availRes.data
            const list = Array.isArray(data) ? data : []
            setAvailability(list.map(a => ({ ...a, days: normalizeDays(a.days) })))
        }).catch(err => console.error(err))
    }, [tutorId])

    useEffect(() => {
        const handleOutside = (e) => {
            if (daysRef.current && !daysRef.current.contains(e.target)) setDaysOpen(false)
        }
        document.addEventListener('mousedown', handleOutside)
        return () => document.removeEventListener('mousedown', handleOutside)
    }, [])

    const toggleDay = (day) => {
        setDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day])
    }

    const resetForm = () => {
        setDays([]); setTime(''); setDuration(''); setErrors({}); setEditingId(null)
    }

    const openAdd = () => { resetForm(); setShowModal(true) }

    const openEdit = (entry) => {
        setEditingId(entry.id)
        setDays([...entry.days])
        setTime(entry.time)
        setDuration(entry.duration)
        setErrors({})
        setShowModal(true)
    }

    const validate = () => {
        const e = {}
        if (days.length === 0) e.days = 'Select at least one day'
        if (!time) e.time = 'Time is required'
        if (!duration) e.duration = 'Duration is required'
        setErrors(e)
        return Object.keys(e).length === 0
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        if (!validate()) return
        setSaving(true)
        const body = { days, time, duration }
        const req = editingId
            ? axiosInstance.put(`/tutors/${tutorId}/availability/${editingId}`, body)
            : axiosInstance.post(`/tutors/${tutorId}/availability`, body)
        req.then(res => {
            const raw = res.data?.data ?? res.data
            const updated = { ...raw, days: normalizeDays(raw.days) }
            if (editingId) {
                setAvailability(prev => prev.map(a => a.id === editingId ? updated : a))
                toast.success('Availability updated!')
            } else {
                setAvailability(prev => [...prev, updated])
                toast.success('Availability added!')
            }
            setShowModal(false)
            resetForm()
        }).catch(err => toast.error(err.response?.data?.message || 'Failed to save'))
          .finally(() => setSaving(false))
    }

    const handleDelete = (id) => {
        axiosInstance.delete(`/tutors/${tutorId}/availability/${id}`)
            .then(() => {
                setAvailability(prev => prev.filter(a => a.id !== id))
                toast.success('Availability removed.')
            })
            .catch(err => toast.error(err.response?.data?.message || 'Failed to delete'))
    }

    return (
        <ContainerDashboard title="Tutor Availability" description="Manage tutor availability schedule">
            <section className="ps-dashboards">
                <div className="p-4 py-5 bg-gradientt rounded-4" style={{ minHeight: '75vh' }}>

                    <div className='d-md-flex justify-content-between align-items-center mb-5'>
                        <div className='d-flex align-items-center gap-3'>
                            <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                style={{ width: 52, height: 52, background: '#A44CD7', color: '#fff', fontWeight: 700, fontSize: 18 }}>
                                {getInitials(tutor?.name || '')}
                            </div>
                            <div>
                                <h5 className='mb-0 fw-bold'>{tutor?.name || 'Tutor'}</h5>
                                <p className='mb-0 text-muted' style={{ fontSize: 13 }}>{tutor?.qualification || ''}</p>
                            </div>
                        </div>
                        <div className='d-flex gap-2 mt-3 mt-md-0'>
                            <button className='button btn-green color-white' onClick={openAdd}>
                                <i className='bi bi-plus-circle me-2'></i>Add Availability
                            </button>
                            <button className='button'
                                style={{ background: '#1a1a2e', color: '#fff', borderRadius: '12px' }}
                                onClick={() => navigate(-1)}>
                                <i className='icon-arrow-left me-2'></i>Back
                            </button>
                        </div>
                    </div>

                    {availability.length === 0 ? (
                        <div className='text-center py-5'>
                            <i className='bi bi-calendar2-x' style={{ fontSize: 52, color: '#ccc' }}></i>
                            <p className='mt-3 text-muted fs-5'>No availability set yet.</p>
                            <button className='button btn-green color-white mt-2' onClick={openAdd}>
                                <i className='bi bi-plus-circle me-2'></i>Add Availability
                            </button>
                        </div>
                    ) : (
                        <div className='row g-3'>
                            {availability.map((entry, idx) => (
                                <div key={entry.id} className='col-md-6'>
                                    <div className='tutor-avail-card'>
                                        <div className='tutor-avail-card-accent' style={{ background: avatarColors[idx % avatarColors.length] }}></div>
                                        <div className='tutor-avail-card-body'>
                                            <div className='d-flex justify-content-between align-items-start'>
                                                <div className='flex-grow-1'>
                                                    <p className='mb-2' style={{ fontSize: 13, color: '#999', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Available On</p>
                                                    <div className='d-flex flex-wrap gap-1 mb-3'>
                                                        {entry.days.map(day => (
                                                            <span key={day} className='tutor-avail-day-badge'>{day}</span>
                                                        ))}
                                                    </div>
                                                    <div className='d-flex gap-4 flex-wrap'>
                                                        <div className='d-flex align-items-center gap-2'>
                                                            <i className='bi bi-clock' style={{ color: 'var(--primary)', fontSize: 15 }}></i>
                                                            <span style={{ fontWeight: 600, fontSize: 15 }}>{formatTime(entry.time)}</span>
                                                        </div>
                                                        <div className='d-flex align-items-center gap-2'>
                                                            <i className='bi bi-hourglass-split' style={{ color: 'var(--primary)', fontSize: 15 }}></i>
                                                            <span style={{ fontWeight: 600, fontSize: 15 }}>{entry.duration}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className='d-flex gap-2 ms-3'>
                                                    <button className='tutor-avail-action-btn edit' onClick={() => openEdit(entry)} title='Edit'>
                                                        <i className='bi bi-pencil'></i>
                                                    </button>
                                                    <button className='tutor-avail-action-btn delete' onClick={() => handleDelete(entry.id)} title='Delete'>
                                                        <i className='bi bi-trash'></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {showModal && (
                <div className='tutor-modal-overlay' onClick={(e) => { if (e.target === e.currentTarget) { setShowModal(false); resetForm() } }}>
                    <div className='tutor-modal'>
                        <div className='tutor-modal-header'>
                            <h5 className='mb-0'>
                                <i className={`bi bi-${editingId ? 'pencil-square' : 'plus-circle'} me-2`}></i>
                                {editingId ? 'Edit Availability' : 'Add Availability'}
                            </h5>
                            <button className='tutor-modal-close' onClick={() => { setShowModal(false); resetForm() }}>
                                <i className='bi bi-x-lg'></i>
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className='tutor-modal-body'>
                            <div className='row g-3'>

                                <div className='col-12'>
                                    <label className='form-label fw-semibold'>Days <i className='text-danger'>*</i></label>
                                    <div className='tutor-days-wrap' ref={daysRef}>
                                        <button type='button' className='tutor-days-btn'
                                            onClick={() => setDaysOpen(o => !o)}>
                                            <span className={days.length === 0 ? 'text-muted' : ''}>
                                                {days.length === 0 ? 'Select Days' : days.join(', ')}
                                            </span>
                                            <i className={`bi bi-chevron-${daysOpen ? 'up' : 'down'}`} style={{ fontSize: 11 }}></i>
                                        </button>
                                        {daysOpen && (
                                            <div className='tutor-days-panel'>
                                                {ALL_DAYS.map(day => (
                                                    <label key={day} className='tutor-day-option'>
                                                        <input type='checkbox'
                                                            checked={days.includes(day)}
                                                            onChange={() => toggleDay(day)} />
                                                        {day}
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    {errors.days && <small className='text-danger'>{errors.days}</small>}
                                </div>

                                <div className='col-md-6'>
                                    <label className='form-label fw-semibold'>Time <i className='text-danger'>*</i></label>
                                    <input type='time' className='form-control tutor-time-input'
                                        value={time} onChange={(e) => setTime(e.target.value)} />
                                    {errors.time && <small className='text-danger'>{errors.time}</small>}
                                </div>

                                <div className='col-md-6'>
                                    <label className='form-label fw-semibold'>Duration <i className='text-danger'>*</i></label>
                                    <select className='form-control tutor-duration-select' value={duration} onChange={(e) => setDuration(e.target.value)}>
                                        <option value=''>Select Duration</option>
                                        {DURATIONS.map(d => (
                                            <option key={d} value={d}>{d}</option>
                                        ))}
                                    </select>
                                    {errors.duration && <small className='text-danger'>{errors.duration}</small>}
                                </div>

                            </div>
                            <div className='d-flex justify-content-end gap-2 pt-3 mt-3' style={{ borderTop: '1px solid #f0f0f0' }}>
                                <button type='button' className='button'
                                    style={{ background: '#6c757d', color: '#fff', borderRadius: '10px' }}
                                    onClick={() => { setShowModal(false); resetForm() }}>
                                    Cancel
                                </button>
                                <button type='submit' className='button btn-green color-white' disabled={saving}>
                                    <i className={`bi bi-${saving ? 'arrow-repeat rotation' : 'check-circle'} me-2`}></i>
                                    {saving ? 'Saving...' : editingId ? 'Update' : 'Add'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </ContainerDashboard>
    )
}

export default TutorAvailability
